/**
 * dd-pdp-display — display-only behaviour for the bespoke product-column pieces.
 *
 * WHY THIS FILE EXISTS, AND WHY IT IS NOT dd-pdp-addons.js
 * -------------------------------------------------------
 * dd-pdp-addons.js wires `document.addEventListener("submit", onSubmit, true)`:
 * a capture-phase interceptor on EVERY /cart/add form in the document, able to
 * preventDefault() and POST to the cart itself. That is correct for the mode it
 * was written for (add_to_cart), and unacceptable as a side effect of wanting a
 * clock to tick.
 *
 * Loading addons.js just to animate a countdown would re-arm that interceptor on
 * a page with a real, live cart form. The only thing standing between a shopper
 * and an unintended cart write would be `if (!items.length) return;` — an
 * empty-list guard, on a cart form. "Very probably inert" is not the standard.
 *
 * So the capability is REMOVED rather than guarded. This file carries everything
 * the display brief needs — the countdown, and (added by later phases) the
 * savings badge and the size-swatch stamp — and contains NO CART CODE AT ALL:
 * no submit listener, no fetch, no /cart/add string, no line-item construction.
 * dd-pdp-addons.js keeps the cart path and is loaded only in add_to_cart mode,
 * which nothing on this template uses.
 *
 * The invariant is greppable and worth keeping that way: this file must never
 * reference the cart-add endpoint, the form-replay call, or a listener on form
 * submission. valyou-verify-dream-block-is-display-only.mjs fetches this file's
 * SERVED source and fails if it finds any of them.
 *
 * Which is why none of those three tokens is spelled out literally in this
 * comment. Shopify minifies theme JS and strips comments, so today a comment
 * naming them would be invisible to that scan — but only by luck. The repo has
 * already been bitten twice by a comment satisfying a later grep; this is the
 * same trap pointing the other way, and the fix is not to write the strings.
 *
 * DELEGATION. Impact re-renders the whole product column on every variant change
 * (it dispatches `product:rerender`), so a listener bound to an element inside
 * the column is gone the first time a shopper picks a size. Everything here is
 * either delegated off `document` or re-run on `product:rerender`.
 *
 * Phase 01 (2026-08-31) proved the re-render is destructive, not in-place: a
 * sentinel node appended to `price-list` was GONE from the document after a
 * variant change. Re-running on `product:rerender` is mandatory, not defensive.
 */
(() => {
  "use strict";

  // The three snippets that need this script each emit their own <script src>
  // tag, because each is an independent block that may be present without the
  // others. Duplicate <script src> tags both execute, which would double-bind
  // every listener and start a second setInterval. Idempotence, once, here.
  if (window.__ddPdpDisplayLoaded) return;
  window.__ddPdpDisplayLoaded = true;

  const each = (sel, fn, scope) =>
    Array.prototype.forEach.call((scope || document).querySelectorAll(sel), fn);
  const pad = (n) => String(n).padStart(2, "0");

  // --- countdown ----------------------------------------------------------
  //
  // Two deadline forms are accepted:
  //
  //   'daily'   evergreen — end of TODAY in the viewer's own timezone.
  //   ISO-8601  a real fixed deadline.
  //
  // Evergreen is computed CLIENT-SIDE and recomputed on every tick, deliberately.
  // Valyou stamps a server-rendered epoch into inline JS; copying that here would
  // put the deadline inside Shopify's full-page cache, and a cached visitor would
  // be served a stale — eventually expired — timer. Computing it per tick also
  // means a tab left open overnight rolls forward to the new day instead of
  // expiring at midnight.

  function deadlineFor(spec) {
    if (spec === "daily") {
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      return end.getTime();
    }
    return Date.parse(spec);
  }

  /**
   * Render the segmented boxes.
   *
   * DAYS is emitted only when there is at least one whole day left. On the
   * evergreen daily reset there never is, so the timer renders three segments
   * rather than four — a permanently "00 DAYS" box on a timer that resets every
   * night advertises that the countdown is decorative. A real multi-day ISO
   * deadline still gets the reference's full four-box treatment.
   */
  function segmentsFor(secs) {
    const days = Math.floor(secs / 86400);
    const segs = [];
    if (days > 0) segs.push([pad(days), "DAYS"]);
    segs.push([pad(Math.floor((secs % 86400) / 3600)), "HRS"]);
    segs.push([pad(Math.floor((secs % 3600) / 60)), "MIN"]);
    segs.push([pad(secs % 60), "SEC"]);
    return segs;
  }

  function tickCountdown(el) {
    const end = deadlineFor(el.getAttribute("data-dd-countdown"));

    // Kept from the original, and kept deliberately: when a fixed deadline passes
    // the timer disappears and the block stays. A gift that vanishes because
    // nobody updated a date is worse than a gift with no urgency on it. Under the
    // daily sentinel this is unreachable — it is the safety net for a sentinel
    // that failed to parse.
    if (Number.isNaN(end)) { el.hidden = true; return; }
    const left = end - Date.now();
    if (left <= 0) { el.hidden = true; return; }

    const segs = segmentsFor(Math.floor(left / 1000));

    // Rebuild only when the shape changes; otherwise write the digits in place.
    // A full innerHTML rewrite every second would blow away the boxes mid-repaint
    // and makes the whole strip flicker on low-end hardware.
    const existing = el.querySelectorAll("[data-seg]");
    if (existing.length !== segs.length) {
      el.textContent = "";
      segs.forEach((seg, i) => {
        if (i > 0) {
          const colon = document.createElement("span");
          colon.className = "dd-dream__timer-colon";
          colon.setAttribute("aria-hidden", "true");
          colon.textContent = ":";
          el.appendChild(colon);
        }
        const box = document.createElement("span");
        box.className = "dd-dream__timer-seg";
        box.setAttribute("data-seg", seg[1]);
        const digits = document.createElement("b");
        digits.textContent = seg[0];
        const label = document.createElement("i");
        label.textContent = seg[1];
        box.appendChild(digits);
        box.appendChild(label);
        el.appendChild(box);
      });
    } else {
      segs.forEach((seg, i) => {
        const b = existing[i].querySelector("b");
        if (b && b.textContent !== seg[0]) b.textContent = seg[0];
      });
    }

    el.hidden = false;
  }

  const tickAll = () => each("[data-dd-countdown]", tickCountdown);

  // --- savings badge ------------------------------------------------------
  //
  // The badge renders from dd-savings-badge.liquid as its own block DIRECTLY
  // BELOW the price, and it stays there.
  //
  // An earlier build lifted it into <price-list> at runtime so it sat on the same
  // visual row as the prices. Caleb, 2026-08-31: put it under the price and make
  // it larger, so the figure reads. That removed the only reason the move
  // existed, so the move is gone rather than left in place doing nothing.
  //
  // Deleting it also deletes the fragility it needed: Impact DESTROYS the
  // contents of <price-list> on a variant change (Phase 01 proved it with a
  // sentinel), so the injected node had to be re-placed after every re-render,
  // and `product:rerender` alone was not reliable enough to do it. None of that
  // applies to a block sitting in its own slot — Impact re-renders it in place,
  // server-side, with the correct figure for the new variant.
  //
  // Consequence worth keeping in mind: the JS state and the no-JS state are now
  // IDENTICAL. There is no longer a way for the badge to be positioned wrongly
  // when scripts fail.

  /**
   * Rewrite the figure from the variant payload.
   *
   * The currency symbol is read off the sibling <sale-price>, which Impact
   * re-renders server-side on every variant change, rather than carried forward
   * from the badge's own previous text. Carrying it forward looked equivalent and
   * was not: one bad first paint (a mis-named Liquid filter emitting raw cents)
   * left the badge with no symbol to copy, and every subsequent variant rendered
   * "1,780" with no "$". Read the symbol from something that is always right.
   */
  function currencyPrefix(scope) {
    const el = scope.querySelector("sale-price, .price-list [class*='sale']");
    const text = el ? el.textContent : "";
    // NON-WORD characters only, not merely non-digit. <sale-price> carries a
    // screen-reader label, so its textContent is "Sale price$4,249.99 USD" — a
    // non-digit run happily swallows "price" and the badge rendered "price$1,780".
    // Letters are never part of the symbol we want here.
    const m = String(text).match(/([^\w\s]+)\s*[\d,]/);
    return m ? m[1] : "$";
  }

  function formatMoney(prefix, cents) {
    const dollars = Math.round(cents / 100);
    return prefix + String(dollars).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function syncSavings(variant) {
    each("[data-dd-savings]", (badge) => {
      const amount = badge.querySelector("[data-dd-savings-amount]");
      if (!amount) return;

      // No variant payload (boot, or an event without detail) means the
      // server-rendered figure is already right — leave it alone.
      if (!variant) return;

      const compare = Number(variant.compare_at_price);
      const price = Number(variant.price);
      const saving = Number.isFinite(compare) && Number.isFinite(price) ? compare - price : 0;

      // A zero or negative saving hides the badge outright. Never "$0 off".
      if (!(saving > 0)) {
        badge.hidden = true;
        return;
      }
      const scope = badge.closest(".product-info") || document;
      badge.hidden = false;
      badge.setAttribute("data-dd-savings-cents", String(saving));
      amount.textContent = formatMoney(currencyPrefix(scope), saving);
    });
  }

  // --- bed-size swatches --------------------------------------------------
  //
  // Impact's block-style variant picker exposes NO semantic per-value hook.
  // Research (2026-08-30) enumerated every CSS-only route and all three are wrong
  // in a different way:
  //
  //   data-option-position="1"  separates Size from Pieces, but not King from Queen
  //   id="option-value-N-…"     ORDINAL — reordering the values in Admin silently
  //                             swaps the icons
  //   value="4510151770205"     precise, but it is Abigale's own option-value id;
  //                             hardcoding it makes the rule Abigale-only
  //   label text                CSS cannot match text at all
  //
  // So the hook is stamped here instead: read the legend, read each label's text,
  // write data-dd-size="king|queen" on the label, and let CSS style off that.
  // Semantic, survives reordering, generalises to any product with a Size option,
  // and degrades to plain unmodified buttons when this script does not run.

  const SIZE_VALUES = { king: "king", queen: "queen" };

  function stampSizeSwatches() {
    each(".variant-picker__option", (fieldset) => {
      const legend = fieldset.querySelector("legend");
      // Normalised: accepts "Size", "size", "Bed Size", with or without a colon.
      const name = (legend ? legend.textContent : "").replace(/[:\s]+$/, "").trim();
      if (!/(^|\s)(bed\s+)?size$/i.test(name)) return;

      each(
        "label",
        (label) => {
          // The icon span is EMPTY, so it never pollutes textContent and this
          // read stays correct however many times the stamp re-runs.
          const text = (label.textContent || "").trim().toLowerCase();
          const size = SIZE_VALUES[text];

          // A value that is neither king nor queen is left ALONE — a Twin, Full
          // or California King gets no icon rather than a wrong one.
          if (!size) {
            label.removeAttribute("data-dd-size");
            const stale = label.querySelector(".dd-bed-icon");
            if (stale) stale.remove();
            return;
          }

          label.setAttribute("data-dd-size", size);

          // A REAL ELEMENT, not a ::before.
          //
          // The first build styled label::before and it rendered on top of the
          // label text: Impact already uses .block-swatch::before — it is the
          // diagonal strikethrough on a sold-out value — and the theme sets it
          // `position: absolute`. An absolutely positioned box is out of flow, so
          // it pushed nothing down, the label stayed 38px, and the bed sat behind
          // the word. Taking over a pseudo-element the theme is already using was
          // the mistake; an in-flow span of our own collides with nothing.
          if (!label.querySelector(".dd-bed-icon")) {
            const icon = document.createElement("span");
            icon.className = "dd-bed-icon";
            icon.setAttribute("aria-hidden", "true");
            label.insertBefore(icon, label.firstChild);
          }
        },
        fieldset
      );
    });
  }

  // --- wiring -------------------------------------------------------------
  //
  // NO submit listener. NO cart calls. If either ever appears in this file, the
  // reason this file exists has been undone — see the header.

  document.addEventListener("variant:change", (event) => {
    syncSavings(event.detail && event.detail.variant);
  });

  document.addEventListener("product:rerender", () => {
    stampSizeSwatches();
    tickAll();
  });

  /**
   * Re-stamp the size swatches whenever the column's DOM actually changes.
   *
   * `product:rerender` alone was NOT enough and the four-variant sweep proved it:
   * it fires, but not reliably after Impact has swapped the new markup in, so a
   * handler bound to it runs against the old DOM and does nothing. An observer
   * does not care which event fires or when — it reacts to the swap itself.
   *
   * Guarded against its own writes: inserting the icon span mutates the tree and
   * would otherwise re-trigger the observer forever.
   */
  let stamping = false;
  function observeColumn() {
    const cols = document.querySelectorAll(".product-info");
    if (!cols.length) return;
    const obs = new MutationObserver(() => {
      if (stamping) return;
      stamping = true;
      // Impact rebuilds the picker, so the stamp is lost with it.
      stampSizeSwatches();
      // Re-arm on the next frame rather than synchronously, so the mutations
      // caused by the stamp itself are all drained first.
      requestAnimationFrame(() => { stamping = false; });
    });
    cols.forEach((col) => obs.observe(col, { childList: true, subtree: true }));
  }

  const boot = () => {
    stampSizeSwatches();
    observeColumn();
    tickAll();
    setInterval(tickAll, 1000);
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
